/**
 * The button that lets a document leave.
 *
 * `POST /export` and the whole of exportDoc.ts — the watermark, the count of
 * what nobody has checked, the one refusal — had been built and shipped with
 * nothing anywhere in the interface that called them. The care in that module
 * was reachable only with a network tab open, so in practice a rep who wanted
 * the Protect board on paper pressed Print, and the print stylesheet carries
 * none of it: no name, no timestamp, no token, no front-page notice.
 *
 * Two things about this component are the whole of it.
 *
 * **A 422 is rendered as a decision, not as an error.** `refuseExport` refuses
 * one case: a selection in which nothing at all has been checked. Its reasoning
 * is that a printout whose every line is a guess reads as settled once it is on
 * paper, carried into a meeting by someone who will reasonably assume the tool
 * would not have produced it otherwise. Shown in red beside the word "Error",
 * that reads as a bug and teaches the rep to press again; shown as a sentence
 * explaining what would have to be true, it sends them to the two-second
 * confirm button on the same screen. Same status code, opposite outcome.
 *
 * **The document actually arrives.** A preview a person cannot get out of is a
 * demonstration. Download writes the markdown to a file; Copy puts it on the
 * clipboard; and the text sits in a selectable box because
 * `navigator.clipboard` is undefined outside a secure context and a rep on a
 * school network is exactly the person who will meet that.
 */

import { useState, type CSSProperties } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, type ExportedDocument, type ExportRefused } from '../lib/api.js';

export type ExportKind = 'protect' | 'claims' | 'claims-working';

function filename(kind: ExportKind): string {
  // Dated, because these get forwarded and a second copy a fortnight later
  // must not overwrite the first in someone's downloads folder.
  const day = new Date().toISOString().slice(0, 10);
  const stem =
    kind === 'protect'
      ? 'what-we-stand-to-lose'
      : kind === 'claims-working'
        ? 'our-claims-WORKING-COPY'
        : 'our-claims';
  return `${stem}-${day}.md`;
}

function download(kind: ExportKind, document_: string): void {
  const blob = new Blob([document_], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename(kind);
  // In the document, not detached. Firefox will not dispatch a synthetic click
  // on an element that was never inserted, and the failure is silent: no
  // download, no error, nothing in the console.
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Freed on the next tick rather than immediately: revoking synchronously
  // after click() races the browser's own read of the blob in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportSelection({
  kind,
  label,
}: {
  kind: ExportKind;
  /** What this export is, in the words of the screen it sits on. */
  label: string;
}) {
  const [copied, setCopied] = useState(false);

  const run = useMutation<ExportedDocument | ExportRefused, Error>({
    mutationFn: () => api.exportSelection(kind),
    onSuccess: () => setCopied(false),
  });

  const result = run.data;

  return (
    <div className="no-print">
      <button
        type="button"
        onClick={() => run.mutate()}
        disabled={run.isPending}
        className="hit label underline underline-offset-2"
      >
        {run.isPending ? 'Preparing…' : label}
      </button>

      {run.error ? (
        <p className="mt-2 text-[12px]" style={{ color: 'var(--color-defect)' }}>
          {run.error.message}
        </p>
      ) : null}

      {result?.kind === 'refused' ? (
        /*
          Deliberately not the defect colour and deliberately not the word
          "error". Nothing failed: the tool read what was selected and declined
          to put it on paper. The sentence from the server already says what
          would have to change, so this frames it and adds nothing.
        */
        <div
          className="callout measure mt-3"
          style={{ '--callout-hue': 'var(--color-ink)' } as CSSProperties}
        >
          <span className="callout-glyph" aria-hidden>
            ◔
          </span>
          <div>
            <p className="label m-0">Not exported — nothing here has been checked</p>
            <p className="mt-1 text-[12px] leading-relaxed">{result.reason}</p>
          </div>
        </div>
      ) : null}

      {result?.kind === 'document' ? (
        <div className="panel measure mt-3 p-3">
          <p className="text-[12px] leading-relaxed">
            {result.unverified === 0 ? (
              <>All {result.total} statements in this document have been checked by a representative.</>
            ) : (
              /*
                The count, in the same words the document's own front page
                uses. A person who reads this and then reads the notice at the
                top of the file should not have to reconcile two phrasings of
                the same fact.
              */
              <>
                <span className="font-medium">
                  {result.unverified} of the {result.total} statements
                </span>{' '}
                in this document have not been checked by anyone. They are marked where they
                appear, and the document names you and the time you asked for it.
              </>
            )}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => download(kind, result.document)}
              className="hit border px-2 py-1 text-[12px]"
              style={{ borderColor: 'var(--color-ink)' }}
            >
              Download it
            </button>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(result.document)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
              className="hit label underline"
            >
              {copied ? 'Copied' : 'Copy it'}
            </button>
          </div>

          <label className="label mt-3 block" htmlFor={`export-text-${kind}`}>
            The document
          </label>
          <textarea
            id={`export-text-${kind}`}
            readOnly
            rows={10}
            value={result.document}
            className="mt-1 w-full border p-2 text-[11px] outline-none"
            style={{ borderColor: 'var(--color-rule)', fontFamily: 'var(--font-mono)' }}
          />
        </div>
      ) : null}
    </div>
  );
}
